'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit2, Trash2, Plus, Upload, Search, Loader2 } from 'lucide-react'
import {Loader}  from './Loader'

export default function AssignmentSummary() {
  const [assignmentData, setAssignmentData] = useState({
    assignment_title: '',
    description: '',
    due_date: null,
    questions: []
  })
  const navigate=useNavigate();
  const [editingIndex, setEditingIndex] = useState(-1)
  const [editedQuestion, setEditedQuestion] = useState({ type: 'Essay Type', question_text: '', mcq_options: [] })
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [newQuestion, setNewQuestion] = useState({ type: 'Essay Type', question_text: '', mcq_options: [] })
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const storedData = localStorage.getItem('assignment')
      if (storedData) {
        const parsedData = JSON.parse(storedData)
        if (parsedData.questions && parsedData.questions.length > 0) {
          const firstQuestion = JSON.parse(parsedData.questions[0].question_text)
          setAssignmentData({
            ...parsedData,
            questions: firstQuestion.assignment.questions
          })
        }
      }
      setIsLoading(false)
    }
    fetchData()
  }, [])

  const handleConfirm = () => {
    alert('Assignment shared successfully!')
    navigate('/assignments/new')
  }

  const handleEdit = (index) => {
    setEditingIndex(index)
    setEditedQuestion(assignmentData.questions[index])
  }

  const handleSave = (index) => {
    const updatedQuestions = [...assignmentData.questions]
    updatedQuestions[index] = editedQuestion
    const updatedAssignment = { ...assignmentData, questions: updatedQuestions }
    setAssignmentData(updatedAssignment)
    saveToLocalStorage(updatedAssignment)
    setEditingIndex(-1)
  }

  const handleDelete = (index) => {
    const updatedQuestions = assignmentData.questions.filter((_, i) => i !== index)
    const updatedAssignment = { ...assignmentData, questions: updatedQuestions }
    setAssignmentData(updatedAssignment)
    saveToLocalStorage(updatedAssignment)
  }

  const handleAddQuestion = () => {
    setIsAddModalOpen(true)
  }

  const handleSaveNewQuestion = () => {
    const updatedQuestions = [...assignmentData.questions, newQuestion]
    const updatedAssignment = { ...assignmentData, questions: updatedQuestions }
    setAssignmentData(updatedAssignment)
    saveToLocalStorage(updatedAssignment)
    setIsAddModalOpen(false)
    setNewQuestion({ type: 'Essay Type', question_text: '', mcq_options: [] })
  }

  const handleMcqOptionChange = (index, value) => {
    const newOptions = [...(newQuestion.mcq_options || [])]
    newOptions[index] = value
    setNewQuestion({ ...newQuestion, mcq_options: newOptions })
  }

  const handleEditedMcqOptionChange = (index, value) => {
    const newOptions = [...(editedQuestion.mcq_options || [])]
    newOptions[index] = value
    setEditedQuestion({ ...editedQuestion, mcq_options: newOptions })
  }

  const handleUploadAnswerKey = () => {
    setIsUploadModalOpen(true)
  }

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadedFile(file)
    }
  }

  const handleSaveUploadedFile = async () => {
    if (uploadedFile) {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('file', uploadedFile)
      formData.append('projectName', assignmentData.assignment_title)

      try {
        const response = await fetch('http://localhost:5000/api/answerKeyUpload', {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          throw new Error('Failed to upload answer key')
        }

        //const result = await response.json()
        alert('Answer key uploaded successfully!')
        setIsUploadModalOpen(false)
        setUploadedFile(null)
      } catch (error) {
        console.error('Error uploading answer key:', error)
        alert('Error uploading answer key')
      } finally {
        setIsUploading(false)
      }
    }
  }

  const saveToLocalStorage = (data) => {
    const formattedData = {
      ...data,
      questions: [
        {
          question_text: JSON.stringify({ assignment: { questions: data.questions } })
        }
      ]
    }
    localStorage.setItem('assignment', JSON.stringify(formattedData))
  }

  const filteredQuestions = assignmentData.questions.filter(question =>
    question.question_text.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {assignmentData.assignment_title || 'Assignment Summary'}
        </h1>
        
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={handleAddQuestion}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            Add New Question
          </button>
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
        </div>

        <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-4">
          {filteredQuestions.map((question, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-800 mb-2">
                    {question.question_text.split('.')[0]}
                  </h2>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {question.question_text.split('.').slice(1).join('.').trim()}
                  </p>
                  {question.type === 'MCQ' && question.mcq_options && question.mcq_options.length > 0 && (
                    <div className="mt-2">
                      {question.mcq_options.map((option, i) => (
                        <div key={i} className="ml-4 text-gray-700">
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    <Edit2 size={18} />
                    <span className="sr-only">Edit question</span>
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 size={18} />
                    <span className="sr-only">Delete question</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-between items-center">
          <button 
            onClick={handleConfirm}
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Confirm and Share
          </button>
          <button 
            onClick={handleUploadAnswerKey}
            className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            Upload Answer Key
          </button>
        </div>
      </div>

      {/* Add Question Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Add New Question</h2>
            <select
              value={newQuestion.type}
              onChange={(e) => setNewQuestion({...newQuestion, type: e.target.value})}
              className="w-full p-2 border rounded mb-4"
            >
              <option value="Essay Type">Essay Type</option>
              <option value="MCQ">Multiple Choice</option>
              <option value="Boolean">Boolean</option>
            </select>
            <textarea
              value={newQuestion.question_text}
              onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
              placeholder="Enter your question here"
              className="w-full h-40 p-2 border rounded mb-4"
              rows={4}
            />
            {newQuestion.type === 'MCQ' && (
              <div className="space-y-2 mb-4">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="flex items-center">
                    <span className="w-8 text-gray-500 font-semibold">{String.fromCharCode(65 + index)}.</span>
                    <input
                      type="text"
                      value={(newQuestion.mcq_options || [])[index] || ''}
                      onChange={(e) => handleMcqOptionChange(index, e.target.value)}
                      className="flex-1 p-2 border rounded"
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveNewQuestion}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Add Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {editingIndex !== -1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Edit Question</h2>
            <select
              value={editedQuestion.type}
              onChange={(e) => setEditedQuestion({...editedQuestion, type: e.target.value})}
              className="w-full p-2 border rounded mb-4"
            >
              <option value="Essay Type">Essay Type</option>
              <option value="MCQ">Multiple Choice</option>
              <option value="Boolean">Boolean</option>
            </select>
            <textarea
              value={editedQuestion.question_text}
              onChange={(e) => setEditedQuestion({...editedQuestion, question_text: e.target.value})}
              className="w-full h-40 p-2 border rounded mb-4"
              rows={4}
            />
            {editedQuestion.type === 'MCQ' && (
              <div className="space-y-2 mb-4">
                {(editedQuestion.mcq_options || []).map((option, index) => (
                  <div key={index} className="flex items-center">
                    <span className="w-8 text-gray-500 font-semibold">{String.fromCharCode(65 + index)}.</span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleEditedMcqOptionChange(index, e.target.value)}
                      className="flex-1 p-2 border rounded"
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setEditingIndex(-1)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleSave(editingIndex)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Answer Key Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-
50">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Upload Answer Key</h2>
            <input 
              type="file" 
              onChange={handleFileUpload}
              accept=".doc,.docx,.pdf,.xls,.xlsx"
              className="mb-4"
            />
            {uploadedFile && (
              <p className="mb-4">Selected file: {uploadedFile.name}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUploadedFile}
                disabled={!uploadedFile || isUploading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

